import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import {
  ShieldCheckIcon,
  CheckIcon,
  ClockTimerIcon,
  MailIcon,
  CameraIcon,
  FileTextIcon,
  StoreIcon,
  BookOpenIcon,
  HelpCircleIcon,
  RefreshCwIcon,
  CheckSquareIcon,
  SquareIcon,
  ArrowRightIcon,
  LogOutIcon,
  UserIcon,
  AlertCircleIcon,
  SparklesIcon,
  CheckBadgeIcon,
  MessageCircleIcon,
  ShoppingBagIcon,
} from "../components/Icons";

function Status({ user, onLogout, onRefresh }) {
  const status = user?.vendorStatus || "pending";

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Interactive checklist state
  const [checklist, setChecklist] = useState([
    { id: 1, text: "Prepare high-resolution product photos on clean white backgrounds", done: true },
    { id: 2, text: "Draft accurate product titles, size charts, and descriptions", done: true },
    { id: 3, text: "Decide competitive retail pricing and profit margins", done: false },
    { id: 4, text: "Prepare your store display name, brand tagline, and logo", done: false },
    { id: 5, text: "Read the IM Online Seller Guide & fulfillment guidelines", done: false },
  ]);

  const toggleChecklist = (id) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, done: !item.done } : item))
    );
  };

  const completedCount = checklist.filter((item) => item.done).length;
  const progressPercent = Math.round((completedCount / checklist.length) * 100);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      setRefreshMsg("Status refreshed just now");
    } catch (err) {
      setRefreshMsg("Unable to refresh. Please check connection.");
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(null), 4000);
    }
  };

  // Format reference ID
  const refId = user?._id
    ? `#IMV-${user._id.slice(-6).toUpperCase()}`
    : "#IMV-849201";

  // Four-Stage Seller Journey
  const journeyStages = [
    {
      num: "01",
      title: "Application Submitted",
      status: "Completed",
      desc: "Your seller registration and initial application details have been received.",
      isDone: true,
      isActive: false,
    },
    {
      num: "02",
      title: "Admin Review",
      status: status === "approved" ? "Completed" : status === "rejected" ? "Needs Attention" : "In Progress",
      desc: "Our merchant team is reviewing your business information and product category.",
      isDone: status === "approved",
      isActive: status === "pending",
      isError: status === "rejected",
    },
    {
      num: "03",
      title: "Verification Complete",
      status: status === "approved" ? "Completed" : "Next Step",
      desc: "Once verified, your merchant account and seller tools are fully activated.",
      isDone: status === "approved",
      isActive: false,
    },
    {
      num: "04",
      title: "Start Selling",
      status: status === "approved" ? "Ready" : "Coming Soon",
      desc: "Set up your storefront, publish footwear products, and start receiving orders.",
      isDone: false,
      isActive: status === "approved",
    },
  ];

  const whatHappensNext = [
    {
      step: "01",
      title: "We Review",
      desc: "Our merchant team evaluates your submitted business and contact information.",
      Icon: FileTextIcon,
    },
    {
      step: "02",
      title: "We Verify",
      desc: "Quick check of your basic identity credentials and fulfillment address.",
      Icon: ShieldCheckIcon,
    },
    {
      step: "03",
      title: "You're Approved",
      desc: "You receive an instant approval notification via email and SMS.",
      Icon: CheckBadgeIcon,
    },
    {
      step: "04",
      title: "Start Selling",
      desc: "Complete your store profile, upload products, and begin receiving orders.",
      Icon: ShoppingBagIcon,
    },
  ];

  const prepCards = [
    {
      Icon: CameraIcon,
      title: "Product Photos",
      desc: "Prepare clear, high-quality images of your footwear collections on clean white or neutral backdrops.",
      linkText: "Photo Guidelines",
      linkTo: "/vendor/requirements",
    },
    {
      Icon: FileTextIcon,
      title: "Product Information",
      desc: "Draft product titles, descriptions, accurate Indian/UK size charts, and competitive selling prices.",
      linkText: "Listing Tips",
      linkTo: "/vendor/requirements",
    },
    {
      Icon: StoreIcon,
      title: "Store Profile",
      desc: "Choose an appealing store name, write a friendly brand description, and prepare your store banner.",
      linkText: "Store Guide",
      linkTo: "/vendor/requirements",
    },
    {
      Icon: BookOpenIcon,
      title: "Learn How Selling Works",
      desc: "Understand order fulfillment workflows, shipping schedules, 0% commission plans, and customer support.",
      linkText: "Seller Guide",
      linkTo: "/vendor/requirements",
    },
  ];

  return (
    <div className="vst-page-root">
      {/* ═══════════════════════ 1. STATUS HEADER ═══════════════════════ */}
      <header className="vst-header">
        <div className="vst-header-inner">
          {/* Brand Link */}
          <Link to="/vendor" className="vst-brand-link">
            <img src={logo} alt="IM Online" className="vst-brand-logo" />
            <div className="vst-brand-text">
              <span className="vst-brand-title">IM ONLINE</span>
              <span className="vst-brand-badge">SELLER CENTER</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="vst-nav-links">
            <Link to="/vendor/requirements" className="vst-nav-link">How It Works</Link>
            <Link to="/vendor/requirements" className="vst-nav-link">Requirements</Link>
            <Link to="/vendor/requirements" className="vst-nav-link">Pricing</Link>
            <Link to="/vendor/requirements" className="vst-nav-link">Seller Guide</Link>
          </nav>

          {/* Right Account & Support Actions */}
          <div className="vst-header-right">
            <a href="mailto:support@imonline.shop" className="vst-help-link">
              <HelpCircleIcon size={16} color="#a85050" />
              <span>Help &amp; Support</span>
            </a>

            {/* Account Dropdown Menu */}
            <div className="vst-account-dropdown-wrap">
              <button
                type="button"
                className="vst-account-btn"
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                aria-expanded={accountMenuOpen}
              >
                <div className="vst-avatar-circle">
                  <UserIcon size={16} color="#ffffff" />
                </div>
                <span className="vst-account-name">
                  {user?.firstName ? `${user.firstName}` : "Seller Account"}
                </span>
                <span className="vst-caret">&#9662;</span>
              </button>

              {accountMenuOpen && (
                <div className="vst-dropdown-menu">
                  <div className="vst-dropdown-header">
                    <strong>{user?.firstName ? `${user.firstName} ${user.lastName || ""}` : "Seller Account"}</strong>
                    <span>{user?.email || "seller@imonline.shop"}</span>
                    <div className="vst-dropdown-status-tag">
                      Status: <strong>{status.toUpperCase()}</strong>
                    </div>
                  </div>
                  <div className="vst-dropdown-divider" />
                  <a href="mailto:support@imonline.shop" className="vst-dropdown-item">
                    <MessageCircleIcon size={15} /> Support Desk
                  </a>
                  <Link to="/vendor/requirements" className="vst-dropdown-item">
                    <BookOpenIcon size={15} /> Seller Requirements
                  </Link>
                  <div className="vst-dropdown-divider" />
                  <button type="button" onClick={onLogout} className="vst-dropdown-item vst-logout-item">
                    <LogOutIcon size={15} /> Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              type="button"
              className="vst-hamburger-btn"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              aria-label="Toggle navigation menu"
            >
              &#9776;
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileNavOpen && (
          <div className="vst-mobile-nav-panel">
            <Link to="/vendor/requirements" onClick={() => setMobileNavOpen(false)} className="vst-mobile-link">
              How It Works
            </Link>
            <Link to="/vendor/requirements" onClick={() => setMobileNavOpen(false)} className="vst-mobile-link">
              Requirements
            </Link>
            <Link to="/vendor/requirements" onClick={() => setMobileNavOpen(false)} className="vst-mobile-link">
              Pricing Plans
            </Link>
            <a href="mailto:support@imonline.shop" className="vst-mobile-link">
              Contact Support
            </a>
            <button type="button" onClick={onLogout} className="vst-mobile-link vst-mobile-logout">
              <LogOutIcon size={16} /> Sign Out ({user?.email})
            </button>
          </div>
        )}
      </header>

      {/* Main Container */}
      <main className="vst-main-container">
        {/* ═══════════════════ 2. HERO / APPLICATION STATUS ═══════════════════ */}
        <section className="vst-hero-card">
          <div className="vst-hero-grid">
            <div className="vst-hero-left">
              <div className="vst-eyebrow-row">
                <span className="vst-pill-badge">SELLER APPLICATION</span>
                <span className="vst-ref-tag">Application ID: <strong>{refId}</strong></span>
              </div>

              {status === "pending" && (
                <>
                  <h1 className="vst-hero-headline">
                    {user?.firstName ? `Good day, ${user.firstName}! ` : ""}
                    You're One Step Closer to Selling on IM Online
                  </h1>
                  <p className="vst-hero-desc">
                    We've received your seller application. Our team is reviewing your business details and will notify you when your store is ready to launch.
                  </p>
                  <div className="vst-status-badge-row">
                    <span className="vst-status-pill pending">
                      <span className="vst-status-pulse" /> UNDER REVIEW
                    </span>
                    <span className="vst-eta-text">
                      <ClockTimerIcon size={15} color="#d97706" /> Typical turnaround: <strong>24–48 business hours</strong>
                    </span>
                  </div>
                </>
              )}

              {status === "approved" && (
                <>
                  <h1 className="vst-hero-headline text-success-bold">
                    You're Approved! Welcome to IM Online Seller Center
                  </h1>
                  <p className="vst-hero-desc">
                    Congratulations! Your seller application has been approved. You now have full access to create your store, upload products, and manage orders.
                  </p>
                  <div className="vst-status-badge-row">
                    <span className="vst-status-pill approved">
                      <CheckIcon size={14} color="#16a34a" /> APPROVED &amp; ACTIVE
                    </span>
                    <Link to="/vendor/dashboard" className="vst-btn-primary">
                      Go to Seller Dashboard <ArrowRightIcon size={16} />
                    </Link>
                  </div>
                </>
              )}

              {status === "rejected" && (
                <>
                  <h1 className="vst-hero-headline text-danger-bold">
                    Your Application Needs Attention
                  </h1>
                  <p className="vst-hero-desc">
                    Our team was unable to verify some of your submitted seller credentials. Please get in touch with our merchant onboarding desk to update your details.
                  </p>
                  <div className="vst-status-badge-row">
                    <span className="vst-status-pill rejected">
                      <AlertCircleIcon size={14} color="#dc2626" /> NOT APPROVED
                    </span>
                    <a href="mailto:support@imonline.shop?subject=Seller Application Re-verification" className="vst-btn-primary">
                      Contact Merchant Support <ArrowRightIcon size={16} />
                    </a>
                  </div>
                </>
              )}

              {status === "suspended" && (
                <>
                  <h1 className="vst-hero-headline text-danger-bold">
                    Your Seller Account Is Suspended
                  </h1>
                  <p className="vst-hero-desc">
                    Your seller account is currently suspended. Please contact our support team to resolve any outstanding compliance or account requirements.
                  </p>
                  <div className="vst-status-badge-row">
                    <span className="vst-status-pill rejected">
                      <AlertCircleIcon size={14} color="#dc2626" /> SUSPENDED
                    </span>
                    <a href="mailto:support@imonline.shop" className="vst-btn-primary">
                      Contact Support <ArrowRightIcon size={16} />
                    </a>
                  </div>
                </>
              )}

              {status === "blocked" && (
                <>
                  <h1 className="vst-hero-headline text-danger-bold">
                    ⛔ Your Store Account Has Been Blocked
                  </h1>
                  <p className="vst-hero-desc">
                    Your seller store and product listings have been blocked by marketplace administration. If you believe this is an error or would like to request re-activation, please reach out to our admin team.
                  </p>
                  <div className="vst-status-badge-row">
                    <span className="vst-status-pill rejected" style={{ background: "#fee2e2", color: "#dc2626", fontWeight: 800 }}>
                      <AlertCircleIcon size={14} color="#dc2626" /> BLOCKED BY ADMIN
                    </span>
                    <a href="mailto:support@imonline.shop?subject=Blocked Account Review" className="vst-btn-primary" style={{ background: "#dc2626", borderColor: "#dc2626" }}>
                      Contact Admin Team <ArrowRightIcon size={16} />
                    </a>
                  </div>
                </>
              )}
            </div>

            {/* Right Hero Graphic: Document + Shield + Checkmark SVG Illustration */}
            <div className="vst-hero-right">
              <div className="vst-verification-art">
                <div className="vst-art-bg-glow" />
                
                {/* SVG Document + Shield Composite */}
                <div className="vst-doc-shield-card">
                  <div className="vst-art-icon-ring">
                    <ShieldCheckIcon size={38} color="#a85050" />
                  </div>
                  <div className="vst-art-lines">
                    <div className="vst-art-line l1" />
                    <div className="vst-art-line l2" />
                    <div className="vst-art-line l3" />
                  </div>
                  <div className="vst-art-verified-pill">
                    <span className="vst-art-check"><CheckIcon size={12} color="#ffffff" /></span>
                    <span>Merchant Verification</span>
                  </div>
                </div>

                <div className="vst-floating-sparkle s1"><SparklesIcon size={18} color="#d97706" /></div>
                <div className="vst-floating-sparkle s2"><CheckBadgeIcon size={20} color="#16a34a" /></div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 3. YOUR SELLER JOURNEY ═══════════════════ */}
        <section className="vst-journey-card">
          <div className="vst-card-header">
            <div>
              <span className="vst-pill-badge-subtle">PROGRESS TIMELINE</span>
              <h2 className="vst-card-title">Your Seller Journey</h2>
              <p className="vst-card-sub">Here's where your application is and what happens next.</p>
            </div>

            {/* Refresh status button with live state feedback */}
            <div className="vst-refresh-box">
              {refreshMsg && <span className="vst-refresh-feedback">{refreshMsg}</span>}
              <button
                type="button"
                className="vst-btn-refresh"
                onClick={handleRefresh}
                disabled={refreshing}
                title="Check latest status from server"
              >
                <RefreshCwIcon size={15} className={refreshing ? "spin-animation" : ""} />
                <span>{refreshing ? "Checking..." : "Refresh Status"}</span>
              </button>
            </div>
          </div>

          {/* Horizontal Desktop / Vertical Mobile Timeline */}
          <div className="vst-timeline-grid">
            {journeyStages.map((stage, idx) => (
              <div
                key={idx}
                className={`vst-timeline-step ${stage.isDone ? "is-done" : ""} ${stage.isActive ? "is-active" : ""} ${stage.isError ? "is-error" : ""}`}
              >
                <div className="vst-step-indicator-row">
                  <div className="vst-step-circle">
                    {stage.isDone ? (
                      <CheckIcon size={16} color="#16a34a" />
                    ) : stage.isError ? (
                      <span className="vst-step-x">✕</span>
                    ) : stage.isActive ? (
                      <span className="vst-step-dot" />
                    ) : (
                      <span className="vst-step-num-text">{stage.num}</span>
                    )}
                  </div>
                  {idx < journeyStages.length - 1 && <div className="vst-step-connector-line" />}
                </div>

                <div className="vst-step-content">
                  <div className="vst-step-header">
                    <span className="vst-step-number-tag">{stage.num}</span>
                    <span className={`vst-step-status-tag ${stage.status.toLowerCase().replace(/\s+/g, "-")}`}>
                      {stage.status}
                    </span>
                  </div>
                  <h3 className="vst-step-title">{stage.title}</h3>
                  <p className="vst-step-desc">{stage.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════ 4. STATUS & WHAT HAPPENS NEXT (2 COLUMNS) ═══════════════════ */}
        <div className="vst-two-col-grid">
          {/* Left Column: Current Status Details & Notification Notice */}
          <div className="vst-side-col">
            {/* Status Summary Card */}
            <div className="vst-info-card">
              <div className="vst-info-card-header">
                <span className="vst-info-card-icon"><ShieldCheckIcon size={20} color="#a85050" /></span>
                <h3 className="vst-info-card-title">Application Status Details</h3>
              </div>

              <div className="vst-detail-rows">
                <div className="vst-detail-row">
                  <span className="vst-detail-label">Application Status</span>
                  <span className={`vst-detail-value status-${status}`}>
                    {status === "pending" ? "● Under Admin Review" : status === "approved" ? "✓ Approved" : status}
                  </span>
                </div>
                <div className="vst-detail-row">
                  <span className="vst-detail-label">Reference ID</span>
                  <span className="vst-detail-value font-mono">{refId}</span>
                </div>
                <div className="vst-detail-row">
                  <span className="vst-detail-label">Registered Email</span>
                  <span className="vst-detail-value">{user?.email || "—"}</span>
                </div>
                <div className="vst-detail-row">
                  <span className="vst-detail-label">Store / Applicant Name</span>
                  <span className="vst-detail-value">{user?.firstName ? `${user.firstName} ${user.lastName || ""}` : "Merchant"}</span>
                </div>
              </div>

              {/* Email Notification Callout */}
              <div className="vst-email-notice-box">
                <div className="vst-email-notice-icon">
                  <MailIcon size={18} color="#a85050" />
                </div>
                <div className="vst-email-notice-text">
                  <strong>We'll keep you updated</strong>
                  <p>You'll receive an email at <u>{user?.email}</u> as soon as our team completes verification.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: What Happens Next */}
          <div className="vst-side-col">
            <div className="vst-info-card">
              <div className="vst-info-card-header">
                <span className="vst-info-card-icon"><ClockTimerIcon size={20} color="#a85050" /></span>
                <h3 className="vst-info-card-title">What Happens Next?</h3>
              </div>

              <p className="vst-info-card-sub">
                Our onboarding team follows a clear and transparent review schedule for every merchant.
              </p>

              <div className="vst-next-steps-list">
                {whatHappensNext.map((item, idx) => (
                  <div key={idx} className="vst-next-step-item">
                    <div className="vst-next-step-badge">{item.step}</div>
                    <div className="vst-next-step-content">
                      <div className="vst-next-step-title-row">
                        <strong className="vst-next-step-title">{item.title}</strong>
                        <item.Icon size={16} color="#888682" />
                      </div>
                      <p className="vst-next-step-desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════ 5. PREPARE WHILE YOU WAIT ═══════════════════ */}
        <section className="vst-section-wrap">
          <div className="vst-section-header">
            <span className="vst-pill-badge-subtle">PROACTIVE PREPARATION</span>
            <h2 className="vst-section-title">Get Ready to Start Selling</h2>
            <p className="vst-section-sub">
              Make the review period productive! Prepare your product catalog and store branding so you can launch instantly upon approval.
            </p>
          </div>

          <div className="vst-prep-cards-grid">
            {prepCards.map((card, idx) => (
              <div key={idx} className="vst-prep-card">
                <div className="vst-prep-icon-box">
                  <card.Icon size={24} color="#a85050" />
                </div>
                <h3 className="vst-prep-title">{card.title}</h3>
                <p className="vst-prep-desc">{card.desc}</p>
                <Link to={card.linkTo} className="vst-prep-link">
                  <span>{card.linkText}</span>
                  <ArrowRightIcon size={14} />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════ 6. SELLER STARTER CHECKLIST ═══════════════════ */}
        <section className="vst-checklist-card">
          <div className="vst-checklist-header">
            <div>
              <span className="vst-pill-badge-subtle">INTERACTIVE CHECKLIST</span>
              <h3 className="vst-checklist-title">Your Seller Starter Checklist</h3>
              <p className="vst-checklist-sub">
                Track your store readiness. Click items as you get them ready!
              </p>
            </div>

            {/* Live Progress Bar */}
            <div className="vst-checklist-progress-box">
              <div className="vst-progress-label">
                <strong>{completedCount}</strong> of <strong>{checklist.length}</strong> Completed
              </div>
              <div className="vst-progress-bar-bg">
                <div className="vst-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </div>

          <div className="vst-checklist-items-grid">
            {checklist.map((item) => (
              <div
                key={item.id}
                className={`vst-checklist-item-row ${item.done ? "is-done" : ""}`}
                onClick={() => toggleChecklist(item.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleChecklist(item.id); }}
              >
                <div className="vst-check-icon-wrap">
                  {item.done ? (
                    <CheckSquareIcon size={22} color="#16a34a" />
                  ) : (
                    <SquareIcon size={22} color="#888682" />
                  )}
                </div>
                <span className="vst-checklist-item-text">{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════ 7. NEED HELP & SUPPORT SECTION ═══════════════════ */}
        <section className="vst-support-card">
          <div className="vst-support-left">
            <span className="vst-pill-badge-subtle">SELLER SUPPORT DESK</span>
            <h3 className="vst-support-title">Need Assistance With Your Application?</h3>
            <p className="vst-support-sub">
              Our merchant onboarding specialists are available Monday to Saturday (9:00 AM – 7:00 PM IST) to answer any queries.
            </p>
          </div>

          <div className="vst-support-actions">
            <a href="mailto:support@imonline.shop?subject=Seller Application Support" className="vst-btn-primary">
              <MailIcon size={16} /> Email Support Desk
            </a>
            <Link to="/vendor/requirements" className="vst-btn-secondary">
              <BookOpenIcon size={16} /> Seller Requirements
            </Link>
          </div>
        </section>
      </main>

      {/* ═══════════════════ 8. CLEAN FOOTER ═══════════════════ */}
      <footer className="vst-footer">
        <div className="vst-footer-inner">
          <div className="vst-footer-left">
            <span>&copy; {new Date().getFullYear()} IM Online Seller Center. All rights reserved.</span>
          </div>
          <div className="vst-footer-links">
            <Link to="/vendor/requirements">How It Works</Link>
            <span>&middot;</span>
            <Link to="/vendor/requirements">Pricing</Link>
            <span>&middot;</span>
            <a href="mailto:support@imonline.shop">Contact Support</a>
            <span>&middot;</span>
            <button type="button" onClick={onLogout} className="vst-footer-logout">Sign Out</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Status;
